'use client'

import { getBooking, saveBooking, updateBooking as updateBookingBackend } from '@/lib/frontent-api'
import React, {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Currency } from '@/types/Page'
import { Domain } from '../../../../domain-config/domain-config'
import LoadingIndicator from '@/components/elements/LoadingIndicator'
import { FieldErrors, useForm, UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { GlobalError } from 'react-hook-form/dist/types/errors'
import { ConfigContext } from '../../../common/ConfigContext'
import {
  BookingFormData,
  BookingFormDataRequest,
  BookingRequestJson,
  BookingRequestSelection,
  BookingRequestType,
  BookingResponseJson,
  BookingResponseStatus,
  Gender,
  getFormSchemaBooking,
  getFormSchemaRequest,
  NO_INSURANCE,
  SafeBookingResponse,
} from '@/types/Booking'
import { debounce } from 'lodash'
import { BookingResult } from '@/components/pages/checkout/booking-page/booking-result/BookingResult'
import { Locale, NUMBER_INPUT_COUNTRIES } from '@/lib/sharedConstants'
import { GetOrUpdateBookingError } from '@/components/pages/checkout/booking-page/booking-result/GetOrUpdateBookingError'
import { useTranslations } from 'next-intl'
import { PaymentMethod } from '@mollie/api-client'
import { ignoredHostForTracking } from '@/lib/utils/utils'
import { defaultCountries, parseCountry } from 'react-international-phone'
import { isInTestGroup } from '@/lib/ab-tests/test-variants'
import { TestVariant } from '@/lib/ab-tests/test-definitions'

function getDialCode(countryCode: string): string {
  const country = defaultCountries.find((c) => parseCountry(c).iso2 === countryCode.toLowerCase())

  return parseCountry(country!).dialCode
}

interface BookingPageContextProps {
  submitHandler: (e?: React.BaseSyntheticEvent) => void
  updateBooking: (info: Partial<BookingRequestSelection>) => void
  booking: BookingResponseJson
  bookingSelection: BookingRequestSelection
  initialBookingData: BookingRequestJson
  formHandler: UseFormReturn<BookingFormData>
  locale: Locale
  bookingRequestInProgress: boolean
  saveBookingResponse: SafeBookingResponse | undefined
  currency: Currency
  withinIframe: boolean
}

interface BookingPageContextInputProps {
  domain: Domain
  currency: Currency
  locale: Locale
  children: ReactElement
  withinIframe: boolean
}

export const BookingPageContext = createContext<BookingPageContextProps>(
  {} as BookingPageContextProps,
)

function handleErrors(response: BookingResponseJson) {
  if (response.Status === BookingResponseStatus.Error) {
    throw new Error('Booking request failed')
  }

  return response
}

function updateDataLayer<T extends { DataLayer: string }>(data: T) {
  try {
    if (typeof window === 'object' && !ignoredHostForTracking()) {
      window.dataLayer = window.dataLayer || []
      const dataLayer = JSON.parse(data.DataLayer) as
        | Record<string, unknown>
        | Record<string, unknown>[]
      if (Array.isArray(dataLayer)) {
        dataLayer.forEach((dataLayerItem) => window.dataLayer!.push(dataLayerItem))
      } else {
        window.dataLayer.push(dataLayer)
      }
    }
  } catch (e) {
    console.error('Failed to update dataLayer', e)
  }

  return data
}

export default function BookingPageContextProvider({
  children,
  domain,
  currency,
  locale,
  withinIframe,
}: BookingPageContextInputProps): ReactElement {
  const [initialBookingData, setInitialBookingData] = useState<BookingRequestJson>()
  const [bookingSelection, setBookingSelection] = useState<BookingRequestSelection>()
  const [bookingData, setBookingData] = useState<BookingResponseJson | undefined>()
  const [loading, setLoading] = useState<boolean>(true)
  const [bookingRequestInProgress, setBookingRequestInProgress] = useState(false)
  const [saveBookingResponse, setSaveBookingResponse] = useState<SafeBookingResponse>()
  const config = useContext(ConfigContext)!
  const t = useTranslations('Booking.BookingFormValidation')
  const bookingType =
    typeof window === 'object' && window.location.pathname.startsWith('/checkout/booking')
      ? BookingRequestType.Booking
      : BookingRequestType.Request
  const abNoTat = isInTestGroup(TestVariant.NoTat) && bookingType === BookingRequestType.Request

  function setBookingAndAdaptSelection(bookingData: BookingResponseJson) {
    setBookingData(bookingData)
    if (bookingData.BookingData) {
      setBookingSelection((selection) => ({
        ...selection!,
        Extras: bookingData.BookingData.Extras.map((extra) => ({
          ExtraCode: extra.Code,
          Quantity: 0,
        })),
      }))
      if (!bookingData.BookingData.InsuranceAllowed) {
        formHandler.setValue('Insurance', NO_INSURANCE)
      }
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    const booking = {
      ProgramCode: params.get('programCode')!,
      RoomCode: params.get('roomCode')!,
      MealPlanCode: +params.get('mealPlanCode')!,
      DateFrom: params.get('dateFrom')!,
      Duration: +params.get('duration')!,
      Beds: +params.get('beds')!,
      Type: bookingType,
      Price: +params.get('price')!,
    }

    setInitialBookingData(booking)
    setBookingSelection({
      RoomCode: params.get('roomCode')!,
      MealPlanCode: +params.get('mealPlanCode')!,
      Extras: [],
      InsuranceCode: '',
      PaymentMethodCode: null,
      FlexBooking: false,
      Reservation: false,
      VoucherCode: undefined,
      MessageToHotel: '',
      Comment: '',
      RapFlex: false,
      RapFlexMinDate: undefined,
      RapFlexMaxDate: undefined,
      Customer: {
        Gender: Gender.Female,
        FirstName: '',
        LastName: '',
        Address: '',
        PostalCode: '',
        City: '',
        CountryCode: 'de',
        Email: '',
        Phone: '',
      },
      Travellers: [
        {
          Gender: Gender.Female,
          FirstName: '',
          LastName: '',
        },
        {
          Gender: Gender.Female,
          FirstName: '',
          LastName: '',
        },
      ],
    })

    void getBooking({
      ...booking,
      domain,
      currency,
    } as unknown as BookingRequestJson & {
      domain: Domain
      currency: Currency
    })
      .then(handleErrors)
      .then(updateDataLayer)
      .then(setBookingAndAdaptSelection)
      .then(() => setLoading(false))
      .catch(() => (window.location.href = '/not-found'))
  }, [])

  const formHandler = useForm<BookingFormData>({
    resolver: zodResolver(
      bookingType === BookingRequestType.Booking
        ? getFormSchemaBooking(!!bookingData?.BookingPrices?.PaymentPrices?.length, t)
        : getFormSchemaRequest(t),
    ),
    mode: 'onBlur', // Triggers validation on blur
    defaultValues: {
      Travelers: [],
      Customer: {
        CountryCode: config.country,
        Iban: '',
        AccountHolder: '',
        DialCode: NUMBER_INPUT_COUNTRIES.includes(domain) ? getDialCode(config.country) : '',
        Phone: '',
      },
      PaymentMethod: '',
      Insurance: '',
      FlightOption: '',
      TermsAndConditionsAccepted: abNoTat,
    },
  })

  const insurance = formHandler.watch('Insurance')
  const paymentMethod = formHandler.watch('PaymentMethod')
  const voucherCode = formHandler.watch('VoucherCode')

  useEffect(() => {
    if (!bookingData) {
      return
    }
    updateBooking({
      InsuranceCode: insurance || NO_INSURANCE,
      PaymentMethodCode: paymentMethod as PaymentMethod,
      VoucherCode: voucherCode,
    })
  }, [insurance, paymentMethod])

  const debouncedFinalizeBooking = useCallback(debounce(finalizeBooking, 250), [
    initialBookingData,
    bookingSelection,
    formHandler,
  ])

  function submitHandler(data: BookingFormData) {
    setBookingRequestInProgress(true)
    void debouncedFinalizeBooking(data)
  }

  function finalizeBooking(data: BookingFormData) {
    const bookingSelectionWithCustomerAndComment = {
      ...bookingSelection!,
      Comment: formHandler.getValues('Comment') || '',
      RapFlex: formHandler.getValues('RapFlex') || undefined,
      RapFlexMinDate: formHandler.getValues('RapFlexMinDate') || undefined,
      RapFlexMaxDate: formHandler.getValues('RapFlexMaxDate') || undefined,
      Customer: { ...data.Customer, Gender: +data.Customer.Gender },
      Travellers: formHandler.getValues('Travelers').map((traveler) => ({
        ...traveler,
        Gender: +traveler.Gender,
      })),
    }

    return void saveBooking(
      initialBookingData!,
      bookingSelectionWithCustomerAndComment,
      domain,
      currency,
    )
      .then(updateDataLayer)
      .then((r) => setSaveBookingResponse(r))
      .finally(() => setBookingRequestInProgress(false))
  }

  function updatedBookingData(updatedBookingData: Partial<BookingResponseJson>) {
    const voucherPart = updatedBookingData.Voucher
      ? { Voucher: updatedBookingData.Voucher }
      : { Voucher: undefined }
    setBookingData({ ...bookingData!, ...updatedBookingData, ...voucherPart })
  }

  function updateBooking(info: Partial<BookingRequestSelection>): void {
    const updatedBookingSelection = { ...bookingSelection, ...info } as BookingRequestSelection
    setBookingSelection(updatedBookingSelection)
    setBookingRequestInProgress(true)

    void updateBookingBackend(initialBookingData!, updatedBookingSelection, domain, currency)
      .then(updatedBookingData)
      .finally(() => setBookingRequestInProgress(false))
  }

  if (loading || !bookingData) {
    return (
      <div className="relative h-screen w-screen items-center justify-center">
        <LoadingIndicator additionalClassName="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>
    )
  }

  if (bookingData.Status === BookingResponseStatus.Error) {
    return <GetOrUpdateBookingError bookingData={bookingData} />
  }

  return (
    <BookingPageContext.Provider
      value={{
        withinIframe: withinIframe,
        booking: bookingData,
        bookingSelection: bookingSelection!,
        initialBookingData: initialBookingData!,
        updateBooking,
        formHandler,
        locale,
        currency,
        submitHandler: (e: React.BaseSyntheticEvent) => {
          void formHandler.handleSubmit(submitHandler, (e) => {
            const bookingRequest = initialBookingData?.Type === BookingRequestType.Booking
            if (e.Insurance) {
              document
                .getElementById('insurance-scroll-target')
                ?.scrollIntoView({ block: 'center' })
            }
            if (!bookingRequest && (e as FieldErrors<BookingFormDataRequest>).RapFlexMinDate) {
              document.querySelector('[name="RapFlexMinDate"]')?.scrollIntoView({ block: 'center' })
            }
            if (!bookingRequest && (e as FieldErrors<BookingFormDataRequest>).RapFlexMaxDate) {
              document.querySelector('[name="RapFlexMaxDate"]')?.scrollIntoView({ block: 'center' })
            }
          })(e)
        },
        bookingRequestInProgress,
        saveBookingResponse,
      }}
    >
      {saveBookingResponse && <BookingResult />}
      {!saveBookingResponse && children}
    </BookingPageContext.Provider>
  )
}

export function getError(path: string, formHandler: UseFormReturn<BookingFormData>) {
  const errors = formHandler.formState.errors

  const keys = path.split('.').map((key) => (isNaN(+key) ? key : Number(key)))

  return keys.reduce(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
    errors,
  ) as GlobalError | undefined
}
